import type { FC } from 'react';
import { useState } from 'react';
import type { BeneficialOwnerData, BeneficialOwnerResponse } from '@gcv/shared';

import { useObservedComponent } from 'hooks';
import { useCrbCreateBeneficialOwner, useCrbUpdateBeneficialOwner } from 'hooks/crb-beneficial-owners';
import { useInjection } from 'ioc';
import { CrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { BeneficialOwnerModalForm } from 'ui/apps/shared/beneficial-owner/beneficial-owner-form';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onOwnerCreated?: (owner: BeneficialOwnerResponse) => void;
  questionnaireBankId?: string;
  config: { addOrEdit: 'add'; owner: undefined } | { addOrEdit: 'edit'; owner: BeneficialOwnerResponse };
}

export const CrbBeneficialOwnerModal: FC<Props> = useObservedComponent((props) => {
  const { isOpen, onClose, onOwnerCreated, questionnaireBankId, config } = props;
  const [isSaving, setIsSaving] = useState(false);

  const dispensaryStore = useInjection(CrbDispensaryStore);

  const { createBeneficialOwner } = useCrbCreateBeneficialOwner(dispensaryStore.currentDispensary.id);
  const { updateBeneficialOwner } = useCrbUpdateBeneficialOwner(dispensaryStore.currentDispensary.id);

  const saveOwner = (beneficialOwnerData: BeneficialOwnerData) => {
    setIsSaving(true);
    const callbacks = {
      onSuccess: (owner: BeneficialOwnerResponse) => {
        onOwnerCreated?.(owner);
        onClose();
      },
      onSettled: () => setIsSaving(false)
    };
    if (config.addOrEdit === 'add') {
      createBeneficialOwner({ beneficialOwner: beneficialOwnerData, questionnaireBankId }, callbacks);
    } else {
      updateBeneficialOwner(
        { beneficialOwnerId: config.owner.id, beneficialOwner: beneficialOwnerData, questionnaireBankId },
        callbacks
      );
    }
  };

  return (
    <BeneficialOwnerModalForm
      isOpen={isOpen}
      onClose={onClose}
      onSave={saveOwner}
      isSaving={isSaving}
      crbId={dispensaryStore.currentDispensary.id}
      timezone={dispensaryStore.currentDispensary.iana_timezone}
      addOrEdit={config.addOrEdit}
      owner={config.owner}
    />
  );
});
